import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Paper,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Form } from "react-final-form";
import { connect, ConnectedProps } from "react-redux";
import { Navigate } from "react-router-dom";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UrlPathUser } from "../../api/url";
import { thunkCreateUser, thunkUpdateUser } from "../../store/actions/UserActions";
import { IStore } from "../../store/IStore";
import { UserDto, UserGroup } from "../../store/models/user/UserDto";
import { FormInput } from "../atoms/FormInput";
import { FormSelect } from "../atoms/FormSelect";
import ImsPaperHead from "../atoms/ImsPaperHead";
import { nameof, ValidationErrors } from "../atoms/Utils";
import { getOperationalUnitOptions } from "../operationalUnit/OperationalUnitForm";
import { getUserGroupNames } from "./UserTable";
import { canCreateKnistrUser } from "../../store/actions/LoginActions";
import {
  useCheckDuplicateEmail,
  useCheckDuplicateUsername,
  useValidateEmail,
  validatePassword,
} from "../atoms/FieldValidation";
import { useTranslation } from "react-i18next";
import MfaConfiguration from "./MfaConfiguration";

interface UserFormProps {
  user: UserDto;
  showMfa?: boolean;
}

const UserForm = (props: UserFormProps & ThunkProps) => {
  const theme = useTheme();
  const [redirect, setRedirect] = useState(false);
  const validateEmail = useValidateEmail();
  const checkDuplicateEmail = useCheckDuplicateEmail();
  const checkDuplicateUsername = useCheckDuplicateUsername();
  const { t } = useTranslation();

  const saveUser = async (userToSave: UserDto) => {
    let success;
    if (userToSave.userNumber) {
      success = await props.thunkUpdateUser(userToSave);
    } else {
      success = await props.thunkCreateUser(userToSave);
    }
    if (success) {
      setRedirect(true);
    }
  };

  const { user, operationalUnits } = props;
  if (redirect) {
    return <Navigate to={UrlPathUser} />;
  }
  const operationalUnitOptions = getOperationalUnitOptions(undefined, operationalUnits, theme, true, true);

  const validateForm = (values: UserDto) => {
    const errors: ValidationErrors<UserDto> = {};
    if (values.userNumber === undefined) {
      checkDuplicateUsername(values.userName, props.users, errors, "userName");
      validateEmail(values.mailAddress, errors, "mailAddress");
      checkDuplicateEmail(values.mailAddress, props.users, errors, "mailAddress");
    }
    validatePassword(values.password, errors, "password");
    return errors;
  };
  return (
    <Form
      onSubmit={saveUser}
      initialValues={user}
      validate={validateForm}
      mutators={{
        handleRoleUpdate: ([args, checked], state, utils) => {
          const e = args as React.ChangeEvent<HTMLInputElement>;
          const updatedUserGroup = e.target.name as UserGroup;
          utils.changeValue(state, "userGroups", (value: UserGroup[]) =>
            checked ? value.concat(updatedUserGroup) : value.filter((userGroup) => userGroup !== updatedUserGroup)
          );
        },
      }}
      render={({ handleSubmit, submitting, values, form }) => (
        <form onSubmit={handleSubmit}>
          <Paper
            style={{
              padding: theme.spacing(3),
              marginTop: theme.spacing(6),
            }}
          >
            <ImsPaperHead text={t("user.headline")} />
            <Grid container spacing={4}>
              <Grid item container spacing={2}>
                <Grid item md={12}>
                  <b>{t("user.data")}</b>
                </Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name={nameof<UserDto>("userName")}
                    label={t("user.username")}
                  />
                </Grid>

                <Grid item container spacing={2}>
                  <Grid item sm={2}>
                    <FormInput
                      variant="outlined"
                      fullWidth={true}
                      type="text"
                      name={nameof<UserDto>("firstName")}
                      label={t("user.firstName")}
                    />
                  </Grid>
                  <Grid item sm={2}>
                    <FormInput
                      variant="outlined"
                      fullWidth={true}
                      type="text"
                      name={nameof<UserDto>("lastName")}
                      label={t("user.lastName")}
                    />
                  </Grid>
                </Grid>
                <Grid item container spacing={2}>
                  <Grid item sm={4}>
                    <FormInput
                      variant="outlined"
                      fullWidth={true}
                      type="text"
                      name={nameof<UserDto>("mailAddress")}
                      readOnly={user.userNumber !== undefined}
                      label={t("user.mail")}
                    />
                  </Grid>
                </Grid>
                <Grid container item spacing={3}>
                  <Grid item md={4}>
                    <FormSelect
                      label={t("user.operationalUnit")}
                      name={nameof<UserDto>("externalUnitNumber")}
                      options={operationalUnitOptions}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {props.showMfa && (
                <Grid item xs={12}>
                  <MfaConfiguration user={props.user} />
                </Grid>
              )}
              <Grid item container spacing={2}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <b
                      style={{
                        marginBottom: theme.spacing(1),
                      }}
                    >
                      {t("user.role")}
                    </b>
                  </FormLabel>
                  <FormGroup>
                    {getUserGroupNames()
                      .filter((r) => !r.key.startsWith("KNISTR_"))
                      .map((userGroupName) => (
                        <FormControlLabel
                          key={userGroupName.key}
                          control={
                            <Checkbox
                              checked={values.userGroups.includes(userGroupName.key)}
                              onChange={form.mutators.handleRoleUpdate}
                              name={userGroupName.key}
                            />
                          }
                          label={userGroupName.value}
                        />
                      ))}
                  </FormGroup>
                  <FormLabel component="legend" style={{ marginTop: theme.spacing(2) }}>
                    <b>{t("user.roles.knistrAdmin")}</b>
                  </FormLabel>
                  <FormGroup style={{ opacity: canCreateKnistrUser() ? 1 : 0.5 }}>
                    {getUserGroupNames()
                      .filter((r) => r.key.startsWith("KNISTR_"))
                      .map((userGroupName) => (
                        <FormControlLabel
                          key={userGroupName.key}
                          control={
                            <Checkbox
                              checked={values.userGroups.includes(userGroupName.key)}
                              onChange={form.mutators.handleRoleUpdate}
                              name={userGroupName.key}
                              disabled={!canCreateKnistrUser()}
                            />
                          }
                          label={userGroupName.value}
                        />
                      ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
          <Grid container justifyContent="flex-end">
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setRedirect(true)}
              style={{ margin: theme.spacing(2, 0, 2, 2) }}
            >
              {t("common.back")}
            </Button>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              disabled={submitting}
              style={{ margin: theme.spacing(2, 0, 2, 2) }}
            >
              {t("common.save")}
            </Button>
          </Grid>
        </form>
      )}
    />
  );
};

const mapStateToProps = (state: IStore) => ({
  operationalUnits: state.operationalUnits.operationalUnits,
  users: state.users.users,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkUpdateUser,
      thunkCreateUser,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(UserForm);
